import { useState } from "react";
import { useTranslation } from "react-i18next";
// Constants - Functions
import { renderToolTip, parseSecondsToSecondsMinutesHours } from "../../../../../config/functions";
import { levelsByProtocol, isOnlyRGSICUVideoProtocol } from "../../../functions";
// Components
import {
  MainDiv,
  Header,
  VideoButton,
  ProtocolImgDiv,
  ProtocolImg,
  InfoVideoImg,
  ProtocolName,
  SessionInfoDiv,
  SessionInfoText,
  Separator,
  SessionDetailsDiv,
  SessionDetailsButton,
  ArrowImg,
  HitsAndErrorsDiv,
  HitsAndErrorsRow,
  HitsAndErrorsBox,
  HitsAndErrorsText,
} from "./styles";
import ProtocolVideoPopup from "../../../../protocolVideoPopup";
// Assets
import IcoVideo from "../../../../../assets/icons/ico_video.svg";
import IcoInfo from "../../../../../assets/icons/ico_info_blue.svg";
import IcoArrowUp from "../../../../../assets/icons/ico_arrow_up_blue.svg";
import IcoArrowDown from "../../../../../assets/icons/ico_arrow_down_blue.svg";

const ProtocolData = ({
  protocolId,
  protocolImg,
  protocolName,
  protocolVideo,
  protocolDescription,
  sessionsGroup1,
  sessionsGroup2,
  isGroup2Enabled,
}) => {
  const [isProtocolVideoShowed, setIsProtocolVideoShowed] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [levels] = useState(new Array(levelsByProtocol).fill(0).map((_, i) => i));
  const [t] = useTranslation("global");

  const renderProtocolInfo = () => {
    return (
      <>
        <VideoButton onClick={() => setIsProtocolVideoShowed(true)} disabled={!protocolVideo}>
          <ProtocolImgDiv>
            <ProtocolImg src={protocolImg} alt="protocol-img" />
          </ProtocolImgDiv>
          <InfoVideoImg
            src={protocolVideo ? IcoVideo : IcoInfo}
            alt="ico-tooltip"
            data-for="protocolDescription"
            data-tip={protocolDescription}
          />
          {renderToolTip("protocolDescription")}
          <ProtocolName>{protocolName}</ProtocolName>
        </VideoButton>
        {isProtocolVideoShowed && <ProtocolVideoPopup setIsProtocolVideoShowed={setIsProtocolVideoShowed} protocolId={protocolId} />}
      </>
    );
  };

  const renderTrainedSessions = () => {
    const getTrainedSessions = (sessionsGroup) => {
      const patients = [...new Set(sessionsGroup.map((session) => session.PATIENT_ID))];

      const result = Math.round(sessionsGroup.length / patients.length);
      return result ? result : "-";
    };

    const trainedSessionsGroup1 = getTrainedSessions(sessionsGroup1);
    const trainedSessionsGroup2 = getTrainedSessions(sessionsGroup2);

    return (
      <SessionInfoDiv width="120px">
        <SessionInfoText color="#0071BC" align={isGroup2Enabled ? "right" : "center"}>
          {trainedSessionsGroup1}
        </SessionInfoText>
        {isGroup2Enabled && (
          <>
            <Separator background="#808080" />
            <SessionInfoText color="#F15A24" align="left">
              {trainedSessionsGroup2}
            </SessionInfoText>
          </>
        )}
      </SessionInfoDiv>
    );
  };

  const renderAverageTime = () => {
    const getAverageTime = (sessionsGroup) => {
      const patients = [...new Set(sessionsGroup.map((session) => session.PATIENT_ID))];

      const accumulatedSeconds = sessionsGroup.reduce(
        (sum, session) => sum + parseFloat(session["sessionDuration(seconds)"] ? session["sessionDuration(seconds)"] : 0),
        0
      );
      const averageSeconds = accumulatedSeconds / sessionsGroup.length;

      const result = Math.round(averageSeconds / patients.length);
      return Boolean(result) ? parseSecondsToSecondsMinutesHours(result) : "-";
    };

    const averageTimeGroup1 = getAverageTime(sessionsGroup1);
    const averageTimeGroup2 = getAverageTime(sessionsGroup2);

    return (
      <SessionInfoDiv width="162px">
        <SessionInfoText color="#0071BC" align={isGroup2Enabled ? "right" : "center"}>
          {averageTimeGroup1}
        </SessionInfoText>
        {isGroup2Enabled && (
          <>
            <Separator background="#808080" />
            <SessionInfoText color="#F15A24" align="left">
              {averageTimeGroup2}
            </SessionInfoText>
          </>
        )}
      </SessionInfoDiv>
    );
  };

  const renderAccumulatedTime = () => {
    const getAverageTime = (sessionsGroup) => {
      const patients = [...new Set(sessionsGroup.map((session) => session.PATIENT_ID))];

      const accumulatedSeconds = sessionsGroup.reduce(
        (sum, session) => sum + parseFloat(session["sessionDuration(seconds)"] ? session["sessionDuration(seconds)"] : 0),
        0
      );

      const result = Math.round(accumulatedSeconds / patients.length);
      return Boolean(result) ? parseSecondsToSecondsMinutesHours(result) : "-";
    };

    const averageTimeGroup1 = getAverageTime(sessionsGroup1);
    const averageTimeGroup2 = getAverageTime(sessionsGroup2);

    return (
      <SessionInfoDiv width="162px">
        <SessionInfoText color="#0071BC" align={isGroup2Enabled ? "right" : "center"}>
          {averageTimeGroup1}
        </SessionInfoText>
        {isGroup2Enabled && (
          <>
            <Separator background="#808080" />
            <SessionInfoText color="#F15A24" align="left">
              {averageTimeGroup2}
            </SessionInfoText>
          </>
        )}
      </SessionInfoDiv>
    );
  };

  const renderDetailsButton = () => {
    return (
      <SessionDetailsDiv>
        <SessionDetailsButton onClick={() => setShowDetails(!showDetails)}>
          <ArrowImg src={showDetails ? IcoArrowUp : IcoArrowDown} alt="ico-arrow" />
        </SessionDetailsButton>
      </SessionDetailsDiv>
    );
  };

  const renderHitsAndErrorsSection = () => {
    let totalHits1 = "-";
    let totalErrors1 = "-";

    let totalHits2 = "-";
    let totalErrors2 = "-";

    const renderHeader = () => {
      return (
        <HitsAndErrorsRow>
          <HitsAndErrorsBox paddingLeft="16px" topShadow background="#F2F2F2F2">
            <HitsAndErrorsText strong>{t("level")}</HitsAndErrorsText>
          </HitsAndErrorsBox>
          <HitsAndErrorsBox justify="center" topShadow background="#F2F2F2F2" borderLeft="2px solid #fff">
            <HitsAndErrorsText strong>{t("hits")}</HitsAndErrorsText>
          </HitsAndErrorsBox>
          <HitsAndErrorsBox justify="center" topShadow background="#F2F2F2F2">
            <HitsAndErrorsText strong>{t("errors")}</HitsAndErrorsText>
          </HitsAndErrorsBox>
          <HitsAndErrorsBox justify="center" topShadow background="#F2F2F2F2">
            <HitsAndErrorsText strong>{t("rgsicu_successRate")}</HitsAndErrorsText>
          </HitsAndErrorsBox>
        </HitsAndErrorsRow>
      );
    };

    const getSessionByGroupAndLevel = (sessionsGroup, level) => {
      let hits = "-";
      let errors = "-";

      const filteredSessions = sessionsGroup.filter(
        (session) => session.hasOwnProperty(`totalHits_level${level}`) || session.hasOwnProperty(`totalErrors_level${level}`)
      );

      for (const session of filteredSessions) {
        if (session.hasOwnProperty(`totalHits_level${level}`)) {
          const currentHits = Number(session[`totalHits_level${level}`]);
          hits = isNaN(hits) ? currentHits : hits + currentHits;
        }
        if (session.hasOwnProperty(`totalErrors_level${level}`)) {
          const currentErrors = Number(session[`totalErrors_level${level}`]);
          errors = isNaN(errors) ? currentErrors : errors + currentErrors;
        }
      }

      const patients = [...new Set(filteredSessions.map((session) => session.PATIENT_ID))];

      let hitsAndErrorsObj = {};

      if (!isNaN(hits)) hitsAndErrorsObj[`totalHits_level${level}`] = Math.round(hits / patients.length);
      if (!isNaN(errors)) hitsAndErrorsObj[`totalErrors_level${level}`] = Math.round(errors / patients.length);

      return hitsAndErrorsObj;
    };

    const getHits = (session, level) => {
      return session.hasOwnProperty(`totalHits_level${level}`)
        ? Number(session[`totalHits_level${level}`])
        : session.hasOwnProperty(`totalErrors_level${level}`)
        ? 0
        : "-";
    };

    const getErrors = (session, level) => {
      return session.hasOwnProperty(`totalErrors_level${level}`)
        ? Number(session[`totalErrors_level${level}`])
        : session.hasOwnProperty(`totalHits_level${level}`)
        ? 0
        : "-";
    };

    const getSuccessRate = (hits, errors) => {
      return isNaN(hits) || isNaN(errors) ? "-" : `${Math.round((hits / (hits + errors)) * 100)}%`;
    };

    const renderLevels = () => {
      return levels.map((level) => {
        const session1 = getSessionByGroupAndLevel(sessionsGroup1, level);
        const session2 = getSessionByGroupAndLevel(sessionsGroup2, level);

        const hits1 = getHits(session1, level);
        const hits2 = getHits(session2, level);

        const errors1 = getErrors(session1, level);
        const errors2 = getErrors(session2, level);

        const successRate1 = getSuccessRate(hits1, errors1);
        const successRate2 = getSuccessRate(hits2, errors2);

        if (!isNaN(hits1) && !isNaN(errors1)) {
          totalHits1 = isNaN(totalHits1) ? hits1 : hits1 + totalHits1;
          totalErrors1 = isNaN(totalErrors1) ? errors1 : errors1 + totalErrors1;
        }
        if (!isNaN(hits2) && !isNaN(errors2)) {
          totalHits2 = isNaN(totalHits2) ? hits2 : hits2 + totalHits2;
          totalErrors2 = isNaN(totalErrors2) ? errors2 : errors2 + totalErrors2;
        }

        return (
          <HitsAndErrorsRow key={level}>
            <HitsAndErrorsBox paddingLeft="16px" background="#F2F2F2F2">
              <HitsAndErrorsText strong>{level + 1}</HitsAndErrorsText>
            </HitsAndErrorsBox>
            <HitsAndErrorsBox justify="center" background="#FFF" borderBottom="2px solid #F2F2F2F2">
              <HitsAndErrorsText width="50px" color="#0071BC" align={isGroup2Enabled ? "right" : "center"}>
                {hits1}
              </HitsAndErrorsText>
              {isGroup2Enabled && (
                <>
                  <Separator background="#e2e2e2" />
                  <HitsAndErrorsText width="50px" color="#F15A24" align="left">
                    {hits2}
                  </HitsAndErrorsText>
                </>
              )}
            </HitsAndErrorsBox>
            <HitsAndErrorsBox justify="center" background="#FFF" borderBottom="2px solid #F2F2F2F2">
              <HitsAndErrorsText width="50px" color="#0071BC" align={isGroup2Enabled ? "right" : "center"}>
                {errors1}
              </HitsAndErrorsText>
              {isGroup2Enabled && (
                <>
                  <Separator background="#e2e2e2" />
                  <HitsAndErrorsText width="50px" color="#F15A24" align="left">
                    {errors2}
                  </HitsAndErrorsText>
                </>
              )}
            </HitsAndErrorsBox>
            <HitsAndErrorsBox justify="center" background="#FFF" borderBottom="2px solid #F2F2F2F2">
              <HitsAndErrorsText width="50px" color="#0071BC" align={isGroup2Enabled ? "right" : "center"}>
                {successRate1}
              </HitsAndErrorsText>
              {isGroup2Enabled && (
                <>
                  <Separator background="#e2e2e2" />
                  <HitsAndErrorsText width="50px" color="#F15A24" align="left">
                    {successRate2}
                  </HitsAndErrorsText>
                </>
              )}
            </HitsAndErrorsBox>
          </HitsAndErrorsRow>
        );
      });
    };

    const renderTotalLevels = () => {
      const totalSuccessRate1 = getSuccessRate(totalHits1, totalErrors1);
      const totalSuccessRate2 = getSuccessRate(totalHits2, totalErrors2);
      return (
        <HitsAndErrorsRow>
          <HitsAndErrorsBox paddingLeft="16px" background="#F2F2F2F2">
            <HitsAndErrorsText strong>{t("total")}</HitsAndErrorsText>
          </HitsAndErrorsBox>
          <HitsAndErrorsBox justify="center" background="#FFF">
            <HitsAndErrorsText width="50px" color="#0071BC" align={isGroup2Enabled ? "right" : "center"}>
              {totalHits1}
            </HitsAndErrorsText>
            {isGroup2Enabled && (
              <>
                <Separator background="#e2e2e2" />
                <HitsAndErrorsText width="50px" color="#F15A24" align="left">
                  {totalHits2}
                </HitsAndErrorsText>
              </>
            )}
          </HitsAndErrorsBox>
          <HitsAndErrorsBox justify="center" background="#FFF">
            <HitsAndErrorsText width="50px" color="#0071BC" align={isGroup2Enabled ? "right" : "center"}>
              {totalErrors1}
            </HitsAndErrorsText>
            {isGroup2Enabled && (
              <>
                <Separator background="#e2e2e2" />
                <HitsAndErrorsText width="50px" color="#F15A24" align="left">
                  {totalErrors2}
                </HitsAndErrorsText>
              </>
            )}
          </HitsAndErrorsBox>
          <HitsAndErrorsBox justify="center" background="#FFF">
            <HitsAndErrorsText width="50px" color="#0071BC" align={isGroup2Enabled ? "right" : "center"}>
              {totalSuccessRate1}
            </HitsAndErrorsText>
            {isGroup2Enabled && (
              <>
                <Separator background="#e2e2e2" />
                <HitsAndErrorsText width="50px" color="#F15A24" align="left">
                  {totalSuccessRate2}
                </HitsAndErrorsText>
              </>
            )}
          </HitsAndErrorsBox>
        </HitsAndErrorsRow>
      );
    };

    return (
      <HitsAndErrorsDiv>
        {renderHeader()}
        {renderLevels()}
        {renderTotalLevels()}
      </HitsAndErrorsDiv>
    );
  };

  return (
    <MainDiv>
      <Header>
        {renderProtocolInfo()}
        {renderTrainedSessions()}
        {renderAverageTime()}
        {renderAccumulatedTime()}
        {!isOnlyRGSICUVideoProtocol(protocolId) && renderDetailsButton()}
      </Header>
      {showDetails && renderHitsAndErrorsSection()}
    </MainDiv>
  );
};

export default ProtocolData;
